import { CommonModule, DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TaskDetailService } from '../services/task-detail.service';
import { ActivatedRoute } from '@angular/router';
import { ApproveTaskRequest } from './task-detail.model';
import { MatStepperModule } from '@angular/material/stepper';
import { MatMenuModule } from '@angular/material/menu';
import { PriorityStatus, TableColumnInfo, TypeOfColumn, TableView } from "design-system";
import { MatSnackBar } from '@angular/material/snack-bar';
import { TaskApprovedResponse } from '../../interfaces/task-approved-response';
import { Q8Breakpoints } from 'core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TaskApprovativo, TaskApprovativoRole } from '../../interfaces/task-approvativo.model';
import { TranslocoCurrencyPipe, TranslocoDecimalPipe } from '@jsverse/transloco-locale';

type PriorityValue = 'low' | 'medium' | 'high';

interface PriorityOption {
  label: string;   // UI
  value: PriorityValue; // backend
}

@Component({
  selector: 'task-task-detail',
  imports: [
    CommonModule, DatePipe, FormsModule,
    MatFormFieldModule, MatInputModule,
    MatButtonModule, MatIconModule, MatListModule,
    MatDividerModule, MatTableModule,
    MatCardModule, ReactiveFormsModule,
    MatStepperModule, MatMenuModule,
    PriorityStatus, TranslocoDirective, TranslocoCurrencyPipe, TranslocoDecimalPipe,
    TableView
  ],
  templateUrl: './task-detail.html',
  styleUrl: './task-detail.scss',
})
export class TaskDetail {
  // icone selettore priorità


  task: TaskApprovativo;
  products: any[] = []; // Aggiungere interfaccia
  showAllProducts = false;
  selectedProduct = null;
  displayedColumns: string[] = ['descProdotto', 'codiceProdotto', 'codiceImballo', 'qtaImballi', 'prezzo', 'qtaFatturabile',
                                'pProvvigione', 'pNoProfit', 'pFuoriPremio', 'dataUltimoAcquisto', 'prezzoUltimoAcquisto'];
  totalRoles: TaskApprovativoRole[];
  currentUserCanEdit: boolean = false;

  // Label usata per valutare il risultato
  outcomeApprovedLabel = 'approved';

  // matstepper
  orientation: 'horizontal' | 'vertical' = 'vertical';
  currentStepperIndex: number = 0;

  // Visualizzazione mobile
  isMobile = false;

  columnsInfo: TableColumnInfo[] = [];

  // Approvazione Task
  taskId: string;
  comment = '';
  files: File[] = [];
  isDragOver = false;

  priorityDotClass(value: PriorityValue): string {
    switch (value) {
      case 'low': return 'priority-dot--low';
      case 'medium': return 'priority-dot--medium';
      case 'high': return 'priority-dot--high';
      default: return 'priority-dot--unknown';
    }
  }

  expandedStorico = new Set<number>();

  // Mostra più/meno prop task
  showExtraProps = false;

  constructor(
      private breakpointObserver: BreakpointObserver,
      private taskDetailService: TaskDetailService,
      private readonly route: ActivatedRoute,
      private snackBar: MatSnackBar,
      private transloco: TranslocoService,
    ) {
    //TODO: UNIRE I BRAKPOINT OBSERVER SE POSSIBILE
    this.breakpointObserver
      .observe([`(max-width: ${Q8Breakpoints.tablet}px)`]) // Fino al breakpoint di desktop
      .subscribe(result => {
        this.isMobile = result.matches;
      });

    this.breakpointObserver
      .observe([`(min-width: ${Q8Breakpoints.tablet}px)`])
      .subscribe(result => {
        this.orientation = result.matches ? 'horizontal' : 'vertical';
      });
  }

  ngOnInit(): void {
    this.transloco.selectTranslation('tasks').subscribe(() => {
      this.translateUI();
    });
  }

  ngAfterViewInit() {
    const routeId = this.route.snapshot.paramMap.get('taskId');

    if (!routeId) {
      console.error('ID task mancante in URL');
      return;
    }

    this.taskId = routeId;
    this.loadTask()
  }

  loadTask() {
    this.taskDetailService.loadTaskDetail(this.taskId).subscribe({
      next: (data: TaskApprovativo) => {
        // console.log('TASK:', data);
        if(data) {
          this.task = data;
          this.products = this.transformProducts(data.dettaglio.items);
          this.totalRoles = [...(this.task?.roles ?? []), (this.task?.primaryRole ?? [])];

          this.currentUserCanEdit = data.currentUserCanEdit;

          setTimeout(() => {
            this.progStepper();
          }, 10);
        }
      },
      error: err => {
        console.error('Errore caricamento task', err);
      }
    });
  }

  private translateUI() {

    // Traduzione delle colonne
    this.columnsInfo = [
      {
        columnNameDef: "descrizioneProdotto",
        //label: this.translocoService.translate('tasks.taskList.columnsInfo.title'),
        label: "Descrizione Prodotto",
        type: TypeOfColumn.TEXT,
        labelOnOneLine: true,
      },
      {
        columnNameDef: "codiceProdotto",
        label: "Codice Prodotto",
        type: TypeOfColumn.TEXT,
      },
      {
        columnNameDef: "codiceImballo",
        label: "Codice Imballo",
        type: TypeOfColumn.TEXT,
      },
      {
        columnNameDef: "qtaImballi",
        label: "Quantità Imballi",
        type: TypeOfColumn.NUMBER,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      {
        columnNameDef: "prezzo",
        label: "Prezzo",
        type: TypeOfColumn.CURRENCY,
        labelOnOneLine: true,
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      },
      {
        columnNameDef: "qtaFatturabile",
        label: "Quantità Fatturabile",
        type: TypeOfColumn.NUMBER,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
      {
        columnNameDef: "pProvvigione",
        label: "% Provvigione",
        type: TypeOfColumn.TEXT,
        labelOnOneLine: true,
      },
      {
        columnNameDef: "pNoProfit",
        label: "% No Profit",
        type: TypeOfColumn.TEXT,
      },
      {
        columnNameDef: "pFuoriPremio",
        label: "% Fuori Premio",
        type: TypeOfColumn.TEXT,
      },
      {
        columnNameDef: "dataUltimoAcquisto",
        label: "Data Ultimo Acquisto",
        type: TypeOfColumn.TEXT,
      },
      {
        columnNameDef: "prezzoUltimoAcquisto",
        label: "Prezzo Ultimo Acquisto",
        type: TypeOfColumn.CURRENCY,
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      },

    ];
  }

   openProductDetail(p: any): void {
    this.selectedProduct = p;
  }

  closeProductDetail(): void {
    this.selectedProduct = null;
  }

  onShowAllProducts(): void {
    this.showAllProducts = true;
  }

  private transformProducts(data: any[]): any[] {
    return data.map((item: any) => {
      // Formatta YYYYMMDD senza creare un oggetto Date, evitando conversioni di fuso orario.
      const date = item.dataUltimoAcquisto?.toString();
      const dataUltimoAcquisto = date?.length === 8
        ? `${date.slice(6, 8)}/${date.slice(4, 6)}/${date.slice(0, 4)}`
        : null;

      return {
        ...item,
        pProvvigione: `${item.pProvvigione} %`,
        pNoProfit: `${item.pNoProfit} %`,
        pFuoriPremio: `${item.pFuoriPremio} %`,
        dataUltimoAcquisto,
      };
    });
  }
 

  progStepper(): number {
    this.currentStepperIndex = 0;

    // Controllo se i ruoli sono approved o rejected
    for (const role of this.totalRoles) {
      const outcome = (role.outcome ?? '').trim().toLowerCase();

      if (outcome === this.outcomeApprovedLabel) {
        this.currentStepperIndex++;
      } else {
        //TODO: aggiungere check del caso rejected ??!?
        break;
      }
    }
    return this.currentStepperIndex;
  }

  get selectedStepperIndex(): number {
    return Math.min(this.currentStepperIndex + 1, this.totalRoles?.length ?? 0);
  }

  isApprovedStepperIndex(stepperIndex: number): boolean {
    if (stepperIndex === 0) {
      return true;
    }

    const outcome = this.totalRoles?.[stepperIndex - 1]?.outcome?.trim().toLowerCase();
    return outcome === this.outcomeApprovedLabel;
  }

  isRejectedStepperIndex(stepperIndex: number): boolean {
    const outcome = this.totalRoles?.[stepperIndex - 1]?.outcome?.trim().toLowerCase();
    return outcome === 'rejected' || outcome === 'refused';
  }

  get priorities(): PriorityOption[] {
    const base = 'tasks.taskDetail.approveTask.priority';
    return [
      { value: 'low', label: this.transloco.translate(`${base}.low`) },
      { value: 'medium', label: this.transloco.translate(`${base}.medium`) },
      { value: 'high', label: this.transloco.translate(`${base}.high`) },
    ];
  }

  // Selettore priortà con traduzione
  get selectedPriority(): PriorityOption {
    const current = this.selectedPriorityValue ?? 'low';
    return this.priorities.find(p => p.value === current) ?? this.priorities[0];
  }

  selectedPriorityValue: PriorityValue = 'low';

  selectPriority(priority: PriorityOption): void {
    this.selectedPriorityValue = priority.value;
  }

  getAllegatoFileName(path: string) {
    const fileName = path.split('/').pop();

    if (!fileName) {
      throw new Error(`Nome file mancante per path: ${path}`);
    }
    
    return fileName;
  }

  // Se ce almeno un role con outcome diverso da stringa vuota mostro la sezione storico
  checkForShowStoricSection(): boolean {
    var showSection = false;

    if (this.totalRoles?.length > 0) {
      for(const role of this.totalRoles) {
        // Se ce almeno un outcome valorizzato ed è diverso da stringa vuota
        if(role.outcome && role.outcome != '') {
          showSection = true;
          break;
        }
      }
    }
    return showSection;
  }

  // Cambia ordinamento per avere il piu recente (l'ultimo) come primo elemento
  get reversedRolesList() {
    return [...this.totalRoles]?.reverse(); 
  }

  // Drag e drop
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (event.dataTransfer?.files?.length) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.addFiles(input.files);
      input.value = '';
    }
  }

  private addFiles(fileList: FileList): void {
    const newFiles = Array.from(fileList);
    this.files = [...this.files, ...newFiles];
  }

  removeFile(index: number): void {
    this.files = this.files.filter((_, i) => i !== index);
  }

  // Metodo per gestire l'approvazione o il rifiuto di un task
  onTaskApprovalManagement(isApproved: boolean) {
    const req: ApproveTaskRequest = {
      taskId: this.taskId,
      outcome: isApproved ? 'Approved' : 'Rejected',
      notes: this.comment,
      priorita: this.selectedPriority.value,
      allegati: this.files
    };

    this.taskDetailService.approveTask(req).subscribe({
      next: res => {
        this.resultManagement(res);
      },
      error: err => console.error('ERROR', err),
    });
  }

  // Metodo per gestire il risultato di un approvazione task (approvato o rifiutato)
  private resultManagement(res: TaskApprovedResponse) {
    // Check sul risultato dell'operazione
    if(res.result) {
      this.loadTask();
      this.cleaningFields();
      console.log('OK', res);
    } else {
      this.snackBar.open(`Errore: ${res.log}`, 'Close', { duration: 5000 });
      console.log('Not OK', res);
    }
  }

  downloadAttachment(allegato: any): void {
    const idAttachment = allegato.id;
    

    this.taskDetailService.downloadAttachmentService(this.taskId, idAttachment).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.getAllegatoFileName(allegato.filePath);
        a.click();
        URL.revokeObjectURL(url);
      },
      error: err => console.error('Errore download allegato', err),
    });
  }

  // Metodo per resettare la card di approvazione
  private cleaningFields() {
    this.comment = '';
    this.selectedPriorityValue = 'low';
    this.files = [];
  }

  isLongText(text: string): boolean {
    return (text?.length ?? 0) > 140;
  }

  toggleStorico(i: number): void {
    if (this.expandedStorico.has(i)) this.expandedStorico.delete(i);
    else this.expandedStorico.add(i);
  }

  isExpanded(i: number): boolean {
    return this.expandedStorico.has(i);
  }

  toggleExtraProps(): void {
    this.showExtraProps = !this.showExtraProps;
  }

  //TODO: fix temporaneo per convertire da string a numero customerCreditLimitExceeded
  toNumber(value?: string | number | null): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isNaN(value) ? null : value;
    }

    const normalizedValue = value.includes(',')
      ? value.replace(/\./g, '').replace(',', '.').trim()
      : value.trim();
    const parsedValue = Number(normalizedValue);

    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  get lastApprovedOrRefusedRole(): TaskApprovativoRole | null {
    if (!this.totalRoles?.length) {
      return null;
    }

    for (let i = this.totalRoles.length - 1; i >= 0; i--) {
      const role = this.totalRoles[i];
      const outcome = role?.outcome?.trim().toLowerCase();

      if (outcome === 'approved' || outcome === 'rejected') {
        return role;
      }
    }

    return null;
  }
}
